import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectConfigExpressSettingsName } from '../../store/slices/config/configSelectors';
import { Text } from 'rsuite';
import { BucketData } from 'src/types/tax';

function formatCollectedTaxes(taxes: string[]) {
  if (!taxes?.length) return '';
  if (taxes.length === 1) return taxes[0];
  if (taxes.length === 2) return `${taxes[0]} and ${taxes[1]}`;
  return `${taxes.slice(0, -1).join(', ')}, and ${taxes[taxes.length - 1]}`;
}

interface CollectionAgreementNoticeProps {
  buckets: Array<BucketData>;
}

const CollectionAgreementNotice = ({
  buckets,
}: CollectionAgreementNoticeProps) => {
  const county = useSelector(selectConfigExpressSettingsName);

  const vrbo = buckets?.find((b) => b.key === 'vrbo');
  const airbnb = buckets?.find((b) => b.key === 'airbnb');
  const airbnbAndVrbo = buckets?.find((b) => b.key === 'airbnb+vrbo');

  const message = useMemo(() => {
    if (airbnbAndVrbo) {
      // Airbnb + Vrbo grouped
      const collectedTaxes = formatCollectedTaxes(
        airbnbAndVrbo.collected_taxes
      );
      return (
        `${county} has signed collection agreements with Airbnb and Vrbo.\n\n` +
        `By default, Airbnb will remit ${collectedTaxes} directly for all rentals facilitated through their platform. Some hosts have opted out of this process or are part of the Professional Host program, meaning that they must remit their own ${collectedTaxes} to ${county} directly. Airbnb does not remit other taxes directly.\n\n` +
        `Vrbo will remit ${collectedTaxes} directly for all rentals facilitated through their platform.\n\n` +
        `Please ensure that you follow the directions and report your income by platform below, splitting it into two separate groupings:\n` +
        `- Vrbo Platform + Airbnb Platform (if not part of Professional Host Program)\n` +
        `- All Other Platforms`
      );
    } else if (vrbo && airbnb) {
      // Both Airbnb and Vrbo
      const vrboTaxMsg =
        vrbo.collected_taxes.length > 1
          ? `Vrbo will remit both ${vrbo.collected_taxes.join(' and ')} directly for all rentals facilitated through their platform.`
          : `Vrbo will remit ${vrbo.collected_taxes[0]} directly for all rentals facilitated through their platform.`;
      return (
        `${county} has signed collection agreements with Airbnb and Vrbo.\n\n` +
        `By default, Airbnb will remit ${formatCollectedTaxes(airbnb.collected_taxes)} directly for all rentals facilitated through their platform. Some hosts have opted out of this process or are part of the Professional Host program, meaning that they must remit their own ${formatCollectedTaxes(airbnb.collected_taxes)} to ${county} directly. Airbnb does not remit other taxes directly.\n\n` +
        `${vrboTaxMsg}\n\n` +
        `Please ensure that you follow the directions and report your income by platform below, splitting it into three separate groupings:\n` +
        `- ${vrbo?.label || `Vrbo Platform`}\n` +
        `- ${airbnb?.label || `Airbnb Platform\n (if not part of Professional Host Program)`}\n` +
        `- All Other Platforms`
      );
    } else if (airbnb) {
      // Airbnb only
      const airbnbTaxMsg = airbnb.collected_taxes?.length
        ? `By default, Airbnb will remit ${formatCollectedTaxes(airbnb.collected_taxes)} directly for all rentals facilitated through their platform. Some hosts have opted out of this process or are part of the Professional Host program, meaning that they must remit their own ${formatCollectedTaxes(airbnb.collected_taxes)} to ${county} directly. Airbnb does not remit other taxes directly.`
        : '';
      return (
        `${county} has signed a collection agreement with Airbnb.\n\n` +
        `${airbnbTaxMsg}\n\n` +
        `Please ensure that you follow the directions and report your income by platform below, splitting it into two separate groupings:\n` +
        `- ${airbnb?.label || `Airbnb Platform\n (if not part of Professional Host Program)`}\n` +
        `- All Other Platforms`
      );
    } else if (vrbo) {
      // Vrbo only
      const vrboTaxMsg =
        vrbo.collected_taxes.length > 1
          ? `Vrbo will remit both ${vrbo.collected_taxes.join(' and ')} directly for all rentals facilitated through their platform.`
          : `Vrbo will remit ${vrbo.collected_taxes[0]} directly for all rentals facilitated through their platform.`;
      return (
        `${county} has signed a collection agreement with Vrbo.\n\n` +
        `${vrboTaxMsg}\n\n` +
        `Please ensure that you follow the directions and report your income by platform below, splitting it into two separate groupings:\n` +
        `- ${vrbo?.label || `Vrbo Platform`}\n` +
        `- All Other Platforms`
      );
    }
    return '';
  }, [county, vrbo, airbnb, airbnbAndVrbo]);

  if (
    !buckets?.length ||
    (buckets.length === 1 && buckets[0].key === 'other')
  ) {
    return null;
  }

  if (!message) return null;

  return (
    <div className="ph-8 pv-8 section-highlight">
      <Text className="text-bold">Collection Agreement Notice</Text>
      <Text
        className="paragraph text-small"
        style={{ whiteSpace: 'pre-line' }}
      >
        {message}
      </Text>
    </div>
  );
};

export default CollectionAgreementNotice;
